import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

type MenuItem =
  | { type: "scroll"; label: string; target: string; description?: string }
  | {
      type: "route";
      label: string;
      path: string;
      description?: string;
      hash?: string;
    };

interface DropdownMenuConfig {
  key: string;
  label: string;
  basePath?: string;
  items: MenuItem[];
}

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(
    null
  );
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const atTop = window.scrollY <= 4;
      if (atTop) {
        setIsScrolled(false);
      } else {
        setIsScrolled(true);
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToId = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      return true;
    }
    return false;
  };

  const scrollToIdWithRetry = (id: string, attempts = 12) => {
    if (scrollToId(id)) return;
    if (attempts <= 0) return;
    setTimeout(() => scrollToIdWithRetry(id, attempts - 1), 100);
  };

  const navigateHomeAndScroll = (id: string) => {
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToIdWithRetry(id), 400);
    } else {
      scrollToIdWithRetry(id);
    }
    setIsOpen(false);
    setOpenDropdown(null);
    setOpenMobileDropdown(null);
  };

  const dropdownMenus: DropdownMenuConfig[] = [
    {
      basePath: "/services",
      key: "services",
      label: "Services",
      items: [
        {
          type: "route",
          label: "App Development",
          path: "/services",
          hash: "card-app-development",
          description: "Product pods for modern web platforms",
        },
        {
          type: "route",
          label: "AI Development",
          path: "/services",
          hash: "card-ai-development",
          description: "Copilots, agents, and automation suites",
        },
        {
          type: "route",
          label: "Mobile App Development",
          path: "/services",
          hash: "card-mobile-app-development",
          description: "React Native & Flutter builds",
        },
      ],
    },
    {
      key: "trainings",
      label: "Trainings",
      items: [
        {
          type: "route",
          label: "Full-Stack Development",
          path: "/trainings/full-stack",
          description: "12-week production-grade cohort",
        },
        {
          type: "route",
          label: "Prompt Engineering Session",
          path: "/trainings/prompt-engineering",
          description: "2-day AI enablement workshops",
        },
      ],
    },
    {
      basePath: "/resources",
      key: "resources",
      label: "Talent",
      items: [
        {
          type: "route",
          label: "Figma Designer",
          path: "/resources",
          hash: "card-figma-designer",
        },
        {
          type: "route",
          label: "Web Developer",
          path: "/resources",
          hash: "card-web-developer",
        },
        {
          type: "route",
          label: "Gen-AI Developer",
          path: "/resources",
          hash: "card-gen-ai-developer",
        },
      ],
    },
    {
      basePath: "/marketplace",
      key: "marketplace",
      label: "Marketplace",
      items: [
        {
          type: "route",
          label: "Convira - AI Chatbot",
          path: "/marketplace",
          hash: "card-convira-chatbot",
          description: "AI chatbot for leads & appointments",
        },
      ],
    },
  ];

  const simpleLinks = [
    { label: "About", target: "about" },
    { label: "Contact", target: "requirement-form" },
  ];

  const handleMenuSelection = (item: MenuItem) => {
    if (item.type === "route") {
      const targetPath = item.hash ? `${item.path}#${item.hash}` : item.path;
      navigate(targetPath);
      setIsOpen(false);
      setOpenDropdown(null);
      setOpenMobileDropdown(null);
      if (item.hash) {
        setTimeout(() => scrollToIdWithRetry(item.hash!), 400);
      }
      return;
    }
    navigateHomeAndScroll(item.target);
  };

  const useLightText = !isScrolled && location.pathname === "/";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || location.pathname !== "/"
          ? "bg-white shadow-md"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center py-4 md:space-x-10">
          <div className="flex justify-start lg:w-0 lg:flex-1">
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className={`text-2xl font-bold ${
                useLightText
                  ? "text-white"
                  : "bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"
              }`}
            >
              Dev Stack Marketplace
            </Link>
          </div>

          <div className="-mr-2 -my-2 md:hidden">
            <button
              type="button"
              className={`inline-flex items-center justify-center p-2 rounded-md ${
                isScrolled
                  ? "text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                  : "text-white hover:text-gray-200"
              } focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500`}
              onClick={() => {
                setIsOpen(!isOpen);
                if (isOpen) {
                  setOpenMobileDropdown(null);
                }
              }}
            >
              <span className="sr-only">Open menu</span>
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          <nav className="hidden md:flex items-center space-x-10">
            {dropdownMenus.map((menu) => (
              <div
                key={menu.key}
                className="relative"
                onMouseEnter={() => setOpenDropdown(menu.key)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  onClick={() => {
                    if (menu.basePath) {
                      navigate(menu.basePath);
                      setIsOpen(false);
                      setOpenDropdown(null);
                      setOpenMobileDropdown(null);
                    } else {
                      setOpenDropdown((prev) =>
                        prev === menu.key ? null : menu.key
                      );
                    }
                  }}
                  className={`text-base font-medium transition-colors duration-300 pt-6 pb-4 ${
                    useLightText
                      ? "text-white hover:text-gray-200"
                      : "text-blue-600 hover:text-blue-700"
                  }`}
                >
                  {menu.label}
                </button>
                {openDropdown === menu.key && (
                  <div className="absolute left-0 mt-0 w-80 bg-white rounded-xl shadow-xl border border-gray-100 py-3 z-50">
                    {menu.items.map((item) => (
                      <button
                        key={item.label}
                        onClick={() => handleMenuSelection(item)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50"
                      >
                        <p className="text-sm font-semibold text-gray-900">
                          {item.label}
                        </p>
                        {item.description && (
                          <p className="text-xs text-gray-500">
                            {item.description}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {simpleLinks.map(({ label, target }) => (
              <button
                key={label}
                onClick={() => navigateHomeAndScroll(target)}
                className={`text-base font-medium transition-colors duration-300 pt-6 pb-4 ${
                  useLightText
                    ? "text-white hover:text-gray-200"
                    : "text-blue-600 hover:text-blue-700"
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`${
          isOpen ? "block" : "hidden"
        } md:hidden absolute top-full left-0 w-full bg-white shadow-lg`}
      >
        <div className="pt-2 pb-4 px-4 space-y-1">
          {dropdownMenus.map((menu) => (
            <div key={menu.key}>
              <button
                onClick={() => {
                  if (menu.basePath) {
                    navigate(menu.basePath);
                    setIsOpen(false);
                    setOpenMobileDropdown(null);
                  } else {
                    setOpenMobileDropdown((prev) =>
                      prev === menu.key ? null : menu.key
                    );
                  }
                }}
                className="block w-full text-left px-3 py-2 text-base font-medium text-blue-600 hover:text-blue-700 hover:bg-gray-50"
              >
                {menu.label}
              </button>
              {!menu.basePath && openMobileDropdown === menu.key && (
                <div className="ml-3 border-l border-gray-100 pl-3">
                  {menu.items.map((item) => (
                    <button
                      key={item.label}
                      onClick={() => handleMenuSelection(item)}
                      className="block w-full text-left px-3 py-2 text-base font-medium text-blue-600 hover:text-blue-700 hover:bg-gray-50"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {simpleLinks.map(({ label, target }) => (
            <button
              key={label}
              onClick={() => navigateHomeAndScroll(target)}
              className="block w-full text-left px-3 py-2 text-base font-medium text-blue-600 hover:text-blue-700 hover:bg-gray-50"
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => navigateHomeAndScroll("requirement-form")}
            className="block w-full text-center px-3 py-2 text-base font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors duration-300 mt-4"
          >
            Contact
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
